import { useMutation } from "@tanstack/react-query";
import { api } from "../../../lib/api";

const BASE = "/auth/recuperacao-senha";

export interface DesafioDeRecuperacao {
  /** Assinado pela API; volta junto com o código nas duas etapas seguintes. */
  desafio: string;
  expiraEm: string;
  reenviarEmSegundos: number;
}

export interface ContaRecuperavel {
  id: number;
  login: string;
  cliente: string | null;
}

export interface Redefinicao {
  desafio: string;
  codigo: string;
  idUsuario: number;
  novaSenha: string;
}

/**
 * Pede o código. A resposta é a mesma exista ou não conta com o e-mail: a
 * tela nunca sabe, e por isso nunca diz, se o endereço está cadastrado.
 */
export const usePedirCodigo = () =>
  useMutation({
    mutationFn: async (email: string) =>
      (await api.post<DesafioDeRecuperacao>(BASE, { email })).data,
  });

export const useValidarCodigo = () =>
  useMutation({
    mutationFn: async ({ desafio, codigo }: { desafio: string; codigo: string }) =>
      (await api.post<{ contas: ContaRecuperavel[] }>(`${BASE}/validar`, { desafio, codigo }))
        .data.contas,
  });

export const useRedefinirSenha = () =>
  useMutation({
    mutationFn: async (redefinicao: Redefinicao) =>
      (await api.post<{ login: string }>(`${BASE}/redefinir`, redefinicao)).data.login,
  });

export interface ErroDeRecuperacao {
  status?: number;
  titulo?: string;
  detalhe: string;
}

/**
 * O que a API explicou, em ProblemDetails. 404 e 503 querem dizer a mesma
 * coisa para a tela: a recuperação não está ligada neste ambiente (rota não
 * registrada, ou e-mail desligado).
 */
export const lerErro = (erro: unknown): ErroDeRecuperacao => {
  const resposta = (erro as {
    response?: { status?: number; data?: { title?: string; detail?: string } };
  })?.response;

  if (!resposta)
    return { detalhe: "Sem resposta do servidor. Confira a conexão e tente de novo." };

  return {
    status: resposta.status,
    titulo: resposta.data?.title,
    detalhe:
      resposta.data?.detail ?? resposta.data?.title ?? "Não foi possível concluir. Tente de novo.",
  };
};

export const recuperacaoIndisponivel = (erro: ErroDeRecuperacao) =>
  erro.status === 404 || erro.status === 503;
